import { Component, OnInit, HostBinding, ElementRef } from '@angular/core';
import {ICellRendererAngularComp, IHeaderAngularComp } from 'ag-grid-angular';
import {ICellRendererParams, IHeaderParams} from 'ag-grid-community';

@Component({
  standalone: false,
  selector: 'mu-group-header-v2',
  templateUrl: './mu-group-header-v2.component.html',
  styleUrls: ['./mu-group-header-v2.component.css']
})
export class MuGroupHeaderComponentV2 implements OnInit,ICellRendererAngularComp {
  // Public so the template can read the params (e.g. via the groupDisplayName
  // getter used in the toggle's aria-label).
  public params: ICellRendererParams;
  private planSettings: any;

  constructor(private el: ElementRef<HTMLElement>) { }

  // WCAG 4.1.2 Name, Role, Value (WAI-ARIA Grid pattern): this component is now
  // the FULL renderer for both the "Plan rollup" full-width row and the MU
  // group rows, so in both cases its host is the only child of the role="row"
  // element and that row would otherwise have no gridcell child. Expose the
  // host as a gridcell for every group row so the row structure is valid for
  // screen readers.
  @HostBinding('attr.role')
  get hostRole(): string | null {
    return 'gridcell';
  }

  public get isRollupHeader(){
    return !!this.params.data?.isRollupHeader;

  }
  public get newHireTrainingWeeks(){
    return this.entitySettings?.newHireTrainingWeeks
  }

  public get isRollupCollapsed(){
    if(this.params.context.isRollupCollapsed){
      return this.params.context.isRollupCollapsed();
    }else{
      return false;
    }

  }
  public get entityOid(){
    return this.params.node.allLeafChildren[0].data.entityInfo?.value.oid;
  }

  // Formatted group display name. As a FULL groupRowRenderer this component no
  // longer receives AG Grid's computed `valueFormatted` (that was only set by
  // the default group cell renderer when we used innerRenderer), so ask the
  // host to resolve the label from the loaded entity list, falling back to
  // valueFormatted/node.key if the context helper is unavailable.
  public get groupDisplayName(): string {
    const fromHost = this.params?.context?.getGroupDisplayName?.(this.params?.node);
    return fromHost ?? this.params?.valueFormatted ?? this.params?.node?.key ?? '';
  }

  public get entitySettings(){
    let entities =  this.params.context.entitySettings();
    return entities[this.entityOid];
  }
  public toggleRollupGroup(){
    if(this.params.context.toggleRollupGroup && this.isRollupHeader) {
      this.params.context.toggleRollupGroup();
    }
  }

  // Unified expand/collapse state for the single native <button> toggle used by
  // BOTH the Plan rollup full-width row and the MU group rows (AG Grid's own
  // toggle is hidden — WCAG 2.1.1 / 4.1.2). For the rollup the state comes from
  // the context; for an MU group row it is the group node's own expanded flag.
  public get isExpanded(): boolean {
    if (this.isRollupHeader) {
      return !this.isRollupCollapsed;
    }
    return !!this.params?.node?.expanded;
  }

  // Toggle handler for the single <button>. Rollup rows delegate to the context;
  // MU group rows flip their own AG Grid node expanded state.
  public toggleGroup(): void {
    if (this.isRollupHeader) {
      this.toggleRollupGroup();
      this.restoreToggleFocus();
      return;
    }
    const node = this.params?.node;
    if (node) {
      node.setExpanded(!node.expanded);
    }
    this.restoreToggleFocus();
  }

  // Enter / Space activate the button natively (firing (click)). AG Grid also
  // listens for these keys on the row and moves focus to the NEXT row, so the
  // caret would appear to "jump" after toggling. Stop the event bubbling to the
  // grid (without preventDefault, so the native button activation still fires).
  // Arrow Down / Up move focus OFF the caret and INTO the table body so keyboard
  // users can reach the (now expanded) data rows (WCAG 2.1.1).
  public onToggleKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
      event.stopPropagation();
      return;
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      // Regular MU group carets always step into their own data rows. The Plan
      // rollup only does so when EXPANDED — its data rows (FTE Required, etc.)
      // then sit directly below it. When the rollup is collapsed the next data
      // row belongs to a group, so diving into it would be wrong; leave those
      // arrow keys to AG Grid's default handling.
      if (this.isRollupHeader && !this.isExpanded) return;
      const dir: 1 | -1 = event.key === 'ArrowDown' ? 1 : -1;
      if (this.focusDataCellFromCaret(dir)) {
        event.preventDefault();
        event.stopPropagation();
      }
    }
  }

  // Move focus from this caret into the nearest DATA row (skipping other group /
  // rollup rows) in the given direction, then let AG Grid's native cell
  // navigation take over. Returns false (leaving the key alone) when there is no
  // data row that way or the grid API is unavailable.
  private focusDataCellFromCaret(dir: 1 | -1): boolean {
    const api: any = this.params?.api;
    const fromIndex = this.params?.node?.rowIndex;
    if (!api?.setFocusedCell || fromIndex == null) return false;
    const cols = api.getAllDisplayedColumns?.() ?? [];
    const colId = cols.length ? cols[0].getColId() : null;
    if (!colId) return false;

    // The Plan rollup header and its data rows (FTE Required, ...) render in AG
    // Grid's PINNED TOP section (pinnedTopRowData), NOT the center row model, so
    // getDisplayedRowAtIndex / a plain setFocusedCell would target the wrong
    // rows (the first group's data). Navigate within the pinned-top section and
    // pass rowPinned "top" to setFocusedCell instead.
    if (this.isRollupHeader) {
      const pinnedCount = api.getPinnedTopRowCount?.() ?? 0;
      let idx = fromIndex + dir;
      while (idx >= 0 && idx < pinnedCount) {
        const node = api.getPinnedTopRow?.(idx);
        const isDataRow = !!node && !node.data?.isRollupHeader && !!node.data;
        if (isDataRow) {
          api.setFocusedCell(idx, colId, 'top');
          return true;
        }
        idx += dir;
      }
      return false;
    }

    const rowCount = api.getDisplayedRowCount?.() ?? 0;
    let idx = fromIndex + dir;
    while (idx >= 0 && idx < rowCount) {
      const node = api.getDisplayedRowAtIndex?.(idx);
      const isDataRow = !!node && !node.group && !node.data?.isRollupHeader && !!node.data;
      if (isDataRow) {
        api.ensureIndexVisible?.(idx);
        api.setFocusedCell(idx, colId);
        return true;
      }
      idx += dir;
    }
    return false;
  }

  // Expand/collapse re-renders the group row, so the <button> that had focus is
  // destroyed and keyboard focus is lost (WCAG 2.4.3 Focus Order / 2.1.1). Move
  // focus back onto the freshly rendered toggle for this same row so the caret
  // keeps focus after Enter/Space.
  private restoreToggleFocus(): void {
    // The Plan rollup row lives in AG Grid's pinned floating-top container and
    // shares row-index 0 with the first CENTER data row, so a row-index lookup
    // against the whole grid can resolve to the wrong row and move focus down.
    // For the rollup, always refocus THIS component's own button. Regular MU
    // group rows are re-rendered as NEW DOM (this component instance is
    // destroyed), so they must be resolved by row-index against the live grid.
    const rowIndex = this.params?.node?.rowIndex;
    const findToggle = (): HTMLElement | null => {
      if (!this.isRollupHeader && rowIndex != null) {
        const root =
          this.el.nativeElement.closest<HTMLElement>('.ag-root') ??
          (document.querySelector<HTMLElement>('.ag-root'));
        const byRow = root?.querySelector<HTMLElement>(
          `.ag-row[row-index="${rowIndex}"] mu-group-header-v2 button.ag-group-toggle`
        );
        if (byRow) return byRow;
      }
      // Rollup, or MU group row not yet re-rendered: fall back to this
      // component's own (possibly detached) button.
      return this.el.nativeElement.querySelector<HTMLElement>('button.ag-group-toggle');
    };

    // Expanding a group inserts child rows; AG Grid can take more than one
    // frame to render the replacement group-row DOM, so retry across a few
    // frames until the live toggle exists, then focus it.
    let attempts = 0;
    const tryFocus = () => {
      const btn = findToggle();
      // Only treat an attached, in-document button as the final target; a
      // detached fallback means AG Grid has not re-rendered yet.
      if (btn && btn.isConnected) {
        btn.focus();
        return;
      }
      if (attempts++ < 5) {
        requestAnimationFrame(tryFocus);
      } else {
        btn?.focus();
      }
    };
    requestAnimationFrame(tryFocus);
  }

  ngOnInit(): void {
  }

  agInit(params: any): void {
    this.params = params;
  }

  refresh(params: ICellRendererParams<any>): boolean {
    return false;
  }


  showNhtpDialog(event) {
    event.preventDefault();
    this.params.context.showNhtpDialog(this.entityOid);
  }

  get showNhtpLink() {
    if (typeof this.params.context.showNhtpLink === 'function') {
      return this.params.context.showNhtpLink();
    }
    return false;
  }
}
